import React from 'react';
import { useSelector } from 'react-redux';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import styled from 'styled-components';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { RootState } from '../../../../../../redux/root';
import { deleteClientDocument } from '../../../../../../api/document';
import { dispatch } from '../../../../../../redux/store';
import { setPermitDocuments, setSelectedDocument } from '../../../../../../redux/slices/documentsSlice';

const ActionFiles = () => {
  const { SelectedDocument, PermitDocuments } = useSelector((s: RootState) => s.documents);

  const deleteAttachment = async id => {
    const result = await deleteClientDocument(id);
    if (result.status === 200) {
      const _clientFiles = SelectedDocument.clientFiles.reduce((files, file) => {
        if (file.id !== id) {
          files.push(file);
        }
        return files;
      }, []);
      const _selectedDocument = {
        ...SelectedDocument,
        clientFiles: _clientFiles,
      };
      dispatch(setSelectedDocument(_selectedDocument));

      const _permitDocuments = PermitDocuments.map(doc => {
        if (doc.pk === _selectedDocument.pk) {
          return _selectedDocument;
        }
        return doc;
      });
      dispatch(setPermitDocuments(_permitDocuments));
    }
  };

  const allowDelete: boolean = SelectedDocument.status === 45;
  const files = SelectedDocument.clientFiles || [];

  if (files.length === 0) return null;

  return (
    <Wrapper>
      <Label>Client Files</Label>
      <List>
        {files.map(item => (
          <Item key={item.id}>
            <Leading>
              {allowDelete ? (
                <InsertDriveFileIcon style={{ color: 'var(--neutral-500)', fontSize: 18 }} />
              ) : (
                <CheckCircleIcon style={{ color: 'var(--success-500, #2f9e5e)', fontSize: 18 }} />
              )}
            </Leading>
            <FileLink href={item.file} target="_blank" rel="noreferrer">
              {item.fileName}
            </FileLink>
            {allowDelete && (
              <DeleteBtn
                type="button"
                title="Remove file"
                onClick={() => deleteAttachment(item.id)}
              >
                <DeleteForeverIcon />
              </DeleteBtn>
            )}
          </Item>
        ))}
      </List>
    </Wrapper>
  );
};

export default ActionFiles;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.div`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--neutral-500);
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Item = styled.div`
  display: grid;
  grid-template-columns: 22px minmax(0, 1fr) 28px;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: var(--neutral-0);
  border: 1px solid var(--border);
  border-radius: 8px;
  min-width: 0;
`;

const Leading = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
`;

const FileLink = styled.a`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--primary-700);
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  transition: color 0.15s ease;

  &:hover {
    color: var(--primary-600);
    text-decoration: underline;
  }
`;

const DeleteBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: var(--neutral-500);
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;

  svg {
    font-size: 18px;
  }

  &:hover {
    background: var(--danger-50, #fdecec);
    color: var(--danger-500, #e0423f);
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;
